import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import QuickActionFAB from '../../components/ui/QuickActionFAB';
import StockStatsCards from './components/StockStatsCards';
import InventoryFilters from './components/InventoryFilters';
import StockOverviewTable from './components/StockOverviewTable';
import QuickAdjustmentPanel from './components/QuickAdjustmentPanel';
import RecentActivityLog from './components/RecentActivityLog';

const InventoryControl = () => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showAdjustmentPanel, setShowAdjustmentPanel] = useState(false);
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for inventory stats
  const [stats] = useState({
    totalProducts: 1247,
    totalProductsChange: 3.2,
    lowStockProducts: 23,
    lowStockChange: -12.5,
    outOfStockProducts: 8,
    outOfStockChange: -25.0,
    totalValue: 487650.75,
    totalValueChange: 8.7
  });

  // Mock data for products
  const [products] = useState([
    {
      id: 'prod001',
      name: 'Laptop Dell XPS 13',
      sku: 'DELL-XPS13-001',
      category: 'Electrónicos',
      currentStock: 3,
      minimumStock: 5,
      location: 'A1-01',
      image: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&h=300&fit=crop',
      lastUpdated: new Date(Date.now() - 3600000),
      responsibleEmployee: 'Carlos Martínez'
    },
    {
      id: 'prod002',
      name: 'Monitor Samsung 27"',
      sku: 'SAM-MON27-002',
      category: 'Electrónicos',
      currentStock: 15,
      minimumStock: 8,
      location: 'A1-02',
      image: 'https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=400&h=300&fit=crop',
      lastUpdated: new Date(Date.now() - 7200000),
      responsibleEmployee: 'Ana García'
    },
    {
      id: 'prod003',
      name: 'Silla Ergonómica Herman Miller',
      sku: 'HM-CHAIR-003',
      category: 'Mobiliario',
      currentStock: 0,
      minimumStock: 3,
      location: 'B1-01',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
      lastUpdated: new Date(Date.now() - 10800000),
      responsibleEmployee: 'Luis Rodríguez'
    },
    {
      id: 'prod004',
      name: 'Impresora HP LaserJet Pro',
      sku: 'HP-LJ-PRO-004',
      category: 'Electrónicos',
      currentStock: 8,
      minimumStock: 4,
      location: 'A2-01',
      image: 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=400&h=300&fit=crop',
      lastUpdated: new Date(Date.now() - 14400000),
      responsibleEmployee: 'María López'
    },
    {
      id: 'prod005',
      name: 'Teclado Mecánico Logitech',
      sku: 'LOG-KB-MEC-005',
      category: 'Electrónicos',
      currentStock: 25,
      minimumStock: 10,
      location: 'A1-01',
      image: 'https://images.unsplash.com/photo-1541140532154-b024d705b90a?w=400&h=300&fit=crop',
      lastUpdated: new Date(Date.now() - 18000000),
      responsibleEmployee: 'Carlos Martínez'
    },
    {
      id: 'prod006',
      name: 'Mesa de Oficina Adjustable',
      sku: 'DESK-ADJ-006',
      category: 'Mobiliario',
      currentStock: 2,
      minimumStock: 5,
      location: 'B2-01',
      image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=300&fit=crop',
      lastUpdated: new Date(Date.now() - 21600000),
      responsibleEmployee: 'Ana García'
    }
  ]);

  // Mock data for recent activities
  const [activities] = useState([
    {
      id: 'act001',
      type: 'adjustment',
      productName: 'Laptop Dell XPS 13',
      description: 'Ajuste de stock por recuento de inventario',
      quantity: -2,
      employeeName: 'Carlos Martínez',
      timestamp: new Date(Date.now() - 300000),
      reason: 'inventory-count'
    },
    {
      id: 'act002',
      type: 'withdrawal',
      productName: 'Monitor Samsung 27"',
      description: 'Retirada para proyecto interno',
      quantity: -3,
      employeeName: 'Ana García',
      timestamp: new Date(Date.now() - 900000),
      reason: 'internal-use'
    },
    {
      id: 'act003',
      type: 'receipt',
      productName: 'Teclado Mecánico Logitech',
      description: 'Recepción de nueva mercancía',
      quantity: 15,
      employeeName: 'Luis Rodríguez',
      timestamp: new Date(Date.now() - 1800000),
      reason: 'supplier-delivery'
    },
    {
      id: 'act004',
      type: 'adjustment',
      productName: 'Silla Ergonómica Herman Miller',
      description: 'Corrección por mercancía dañada',
      quantity: -1,
      employeeName: 'María López',
      timestamp: new Date(Date.now() - 3600000),
      reason: 'damaged-goods'
    },
    {
      id: 'act005',
      type: 'transfer',
      productName: 'Mesa de Oficina Adjustable',
      description: 'Transferencia entre almacenes',
      quantity: 0,
      employeeName: 'Carlos Martínez',
      timestamp: new Date(Date.now() - 7200000),
      reason: 'location-transfer'
    }
  ]);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters applied:', newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
    console.log('Filters cleared');
  };

  const handleProductSelect = (product) => {
    console.log('Product selected for details:', product);
    // In real app: open product details modal or navigate to product page
  };

  const handleQuickAdjust = (productIds) => {
    setSelectedProducts(productIds);
    setShowAdjustmentPanel(true);
  };

  const handleAdjustmentSubmit = async (adjustmentData) => {
    console.log('Adjustment submitted:', adjustmentData);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Close panel and reset selection
    setShowAdjustmentPanel(false);
    setSelectedProducts([]);
    
    // In real app: refresh data, show success message
    alert('Ajuste de stock registrado correctamente');
  };

  const handleActivityDetails = (activity) => {
    console.log('View activity details:', activity);
    // In real app: open activity details modal
  };

  const handleRefreshActivities = async () => {
    console.log('Refreshing activities...');
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-text-secondary">Cargando control de inventario...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading-bold text-text-primary mb-2">
              Control de Inventario
            </h1>
            <p className="text-text-secondary">
              Gestiona el stock, realiza ajustes y monitorea la actividad del inventario en tiempo real.
            </p>
          </div>

          {/* Stats Cards */}
          <StockStatsCards stats={stats} />

          {/* Filters */}
          <InventoryFilters
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Stock Overview Table - 6 columns */}
            <div className="lg:col-span-6">
              <StockOverviewTable
                products={products}
                onProductSelect={handleProductSelect}
                onQuickAdjust={handleQuickAdjust}
              />
            </div>

            {/* Quick Adjustment Panel - 3 columns */}
            <div className="lg:col-span-3">
              <QuickAdjustmentPanel
                selectedProducts={selectedProducts}
                onAdjustmentSubmit={handleAdjustmentSubmit}
                onClose={() => {
                  setShowAdjustmentPanel(false);
                  setSelectedProducts([]);
                }}
              />
            </div>

            {/* Recent Activity Log - 3 columns */}
            <div className="lg:col-span-3">
              <RecentActivityLog
                activities={activities}
                onViewDetails={handleActivityDetails}
                onRefresh={handleRefreshActivities}
              />
            </div>
          </div>
        </div>
      </main>

      <QuickActionFAB />
    </div>
  );
};

export default InventoryControl;